import React, { useState, useMemo } from "react";
import { T, money, buzzLight } from "./components";
import {
  WEEKLY_DEALS, WEEKLY_CATEGORIES, WEEKLY_SCAN_WEEK,
  WEEKLY_STORE_LOCATIONS,
} from "./seedData";
import { BrandRail } from "./brands";
import BubbleLogo, { flyerColor } from "./BubbleLogo";
import { haversineKm, formatDistance } from "./location";
import {
  IconLeaf, IconBag, IconTag, IconDrumstick, IconMilk, IconSnow,
  IconCookie, IconBaby, IconSpray, IconSearch, IconCheck, IconInfo,
  IconPin, IconBookmark, IconFlame, Mascot,
} from "./icons";

// ─── Day-granularity date helpers (timezone-safe: never `new Date("2026-10-14")`) ───
function parseDay(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtDay(s) {
  const d = parseDay(s);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}
function fmtRange(a, b) {
  return a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`;
}
function isExpired(deal, today) {
  return parseDay(deal.validTo).getTime() < today.getTime();
}

// ─── "Best price" badge: rank deals sharing a compareKey by per-unit price ───
// Only groups of 2+ with matching compareUnit are ranked; ties all get the badge.
export function computeBestIds(deals) {
  const groups = {};
  for (const d of deals) {
    if (d.compareKey && d.price != null) {
      (groups[d.compareKey] || (groups[d.compareKey] = [])).push(d);
    }
  }
  const best = new Set();
  for (const key of Object.keys(groups)) {
    const g = groups[key];
    if (g.length < 2) continue;
    const unit = g[0].compareUnit;
    if (!unit || !g.every((d) => d.compareUnit === unit)) continue;
    let min = Infinity;
    for (const d of g) min = Math.min(min, d.price / (d.compareQty || 1));
    for (const d of g) {
      if (Math.abs(d.price / (d.compareQty || 1) - min) < 1e-9) best.add(d.id);
    }
  }
  return best;
}

const fmtPts = (n) => Number(n).toLocaleString("en-US");

// ─── Nearest-store distance: min over the store's verified locations ─────
// Stores without a verified coordinate (Safeway, Co-op) return null —
// their cards show no distance chip, never a fabricated distance.
function storeDistanceKm(deal, loc) {
  if (!loc) return null;
  const spots = WEEKLY_STORE_LOCATIONS[deal.storeId];
  if (!spots || spots.length === 0) return null;
  let min = Infinity;
  for (const s of spots) min = Math.min(min, haversineKm(loc.lat, loc.lng, s.lat, s.lng));
  return min;
}

// ─── Category art: flat color block + icon-family watermark ───────────────
// No hotlinked photos, no AI imagery, no gradients — one intentional flat
// color per category with the 1.8px icon family as watermark. Restrained,
// DoorDash-placeholder-grade.
const CAT_STYLE = {
  produce: { c: "#37B24D", Icon: IconLeaf },
  meat: { c: "#E63B2E", Icon: IconDrumstick },
  dairy: { c: "#2E9BE6", Icon: IconMilk },
  pantry: { c: "#FF7A1A", Icon: IconBag },
  frozen: { c: "#12B5A5", Icon: IconSnow },
  snacks: { c: "#8B5CC4", Icon: IconCookie },
  baby: { c: "#F0567C", Icon: IconBaby },
  household: { c: "#FFC61A", Icon: IconSpray },
};

export function CategoryArt({ category, iconSize = 72 }) {
  const c = CAT_STYLE[category] || CAT_STYLE.pantry;
  const Icon = c.Icon;
  return (
    <div
      style={{
        position: "relative", overflow: "hidden", height: "100%", width: "100%",
        background: c.c,
      }}
      aria-hidden="true"
    >
      <div style={artDeco(110, -38, -28, 0.14)} />
      <div style={artDeco(64, null, 34, 0.1, -18)} />
      <div
        style={{
          position: "absolute", right: 8, bottom: 2, color: "#fff",
          opacity: 0.95, transform: "rotate(-8deg)",
        }}
      >
        <Icon size={iconSize} />
      </div>
    </div>
  );
}

function artDeco(size, top, leftOrRight, opacity, bottom) {
  const s = {
    position: "absolute", width: size, height: size, borderRadius: size / 2,
    background: `rgba(255,255,255,${opacity})`,
  };
  if (top != null) s.top = top;
  if (bottom != null) s.bottom = bottom;
  if (typeof leftOrRight === "number" && leftOrRight >= 0) s.left = leftOrRight;
  else s.right = Math.abs(leftOrRight || 0);
  return s;
}

// ─── Flipp photo art: the actual flyer item photo, flyer-style ──────────
// Every card gets an onError fallback to CategoryArt, so a dead/expired
// Flipp CDN link never shows a broken image. Deals without imageUrl
// (hand-transcribed seeds) always render the SVG art.
export function FlippArt({ deal, iconSize = 64 }) {
  const [err, setErr] = useState(false);
  if (!deal.imageUrl || err)
    return <CategoryArt category={deal.category} iconSize={iconSize} />;
  return (
    <div
      style={{
        position: "relative", overflow: "hidden", height: "100%", width: "100%",
        background: "#F5F5F5",
      }}
      aria-hidden="true"
    >
      <img
        src={deal.imageUrl}
        alt=""
        loading="lazy"
        draggable={false}
        onError={() => setErr(true)}
        style={{
          width: "100%", height: "100%", objectFit: "cover", display: "block",
          animation: "fadein 0.4s ease",
        }}
      />
    </div>
  );
}

// ─── Price block: big Flipp-style price + effective-after-points ─────────
// v2.1: price is the dominant element — bigger, tighter, black.
function PriceBlock({ deal, priceSize = 25 }) {
  const po = deal.pointsOffer;
  if (deal.price == null) {
    // Points/offers with no shelf price (e.g. "20,000 pts", "$10 off").
    return (
      <div>
        <div className="tnum" style={{ fontSize: priceSize, fontWeight: 800, color: T.accentDark, lineHeight: 1.15, letterSpacing: -0.5 }}>
          {deal.headline}
        </div>
        {po && po.requirement && (
          <div style={styles.offerLine}>{po.points ? `${fmtPts(po.points)} pts · ` : ""}{po.requirement}</div>
        )}
        {po && po.effectiveNote && <div style={styles.offerNote}>{po.effectiveNote}</div>}
      </div>
    );
  }
  const effUnit = deal.unit === "/lb" ? "/lb" : " ea";
  return (
    <div>
      <div className="tnum" style={{ fontSize: priceSize, fontWeight: 800, color: T.text, lineHeight: 1.15, letterSpacing: -0.5 }}>
        {money(deal.price)}
        {deal.unit && (
          <span style={{ fontSize: Math.round(priceSize * 0.55), fontWeight: 600, color: T.textMuted }}>
            {deal.unit}
          </span>
        )}
        {deal.regularPrice != null && deal.regularPrice > deal.price && (
          <span className="tnum" style={{ fontSize: Math.round(priceSize * 0.55), fontWeight: 600, color: T.textMuted, textDecoration: "line-through", marginLeft: 7 }}>
            {money(deal.regularPrice)}
          </span>
        )}
      </div>
      {po && po.effectivePrice != null && (
        <div className="tnum" style={styles.effLine}>
          {money(po.effectivePrice)}{effUnit} after points
        </div>
      )}
      {po && po.effectivePrice == null && po.requirement && (
        <div style={styles.offerLine}>
          {po.points ? `+${fmtPts(po.points)} pts · ` : ""}{po.requirement}
        </div>
      )}
      {po && po.effectiveNote && <div style={styles.offerNote}>{po.effectiveNote}</div>}
    </div>
  );
}

// ─── "This week's best" hero: the decide-for-me lure ──────────────────────
// Computed, never hand-picked: rank live deals by discount depth
// (regularPrice → price), best-price badge second. Max 3 per store so the
// lure isn't one store's wall. Survives the Thursday data refresh.
function heroPicks(deals, bestIds, today, n = 6) {
  const scored = [];
  for (const d of deals) {
    if (isExpired(d, today)) continue;
    if (d.regularPrice == null || d.price == null || d.regularPrice <= d.price) continue;
    scored.push({ d, pct: (d.regularPrice - d.price) / d.regularPrice });
  }
  scored.sort(
    (a, b) =>
      b.pct - a.pct ||
      (bestIds.has(b.d.id) ? 1 : 0) - (bestIds.has(a.d.id) ? 1 : 0)
  );
  const perStore = {};
  const out = [];
  for (const s of scored) {
    const k = s.d.storeId || s.d.store;
    if ((perStore[k] || 0) >= 3) continue;
    perStore[k] = (perStore[k] || 0) + 1;
    out.push(s);
    if (out.length >= n) break;
  }
  return out;
}

// ─── v3.8 flyer helpers ──────────────────────────────────────────────────
// Stable vibrant color per deal (hash of id) + an honest flyer headline
// built only from real fields: discount % when a regular price exists,
// the deal's own headline for points offers, else "Weekly Special".
function cardColor(deal) {
  let h = 0;
  const s = deal.id || deal.item || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return flyerColor(h);
}
function discountPct(deal) {
  if (deal.price == null || deal.regularPrice == null || deal.regularPrice <= deal.price)
    return null;
  return Math.round((1 - deal.price / deal.regularPrice) * 100);
}
function flyerHeadline(deal, best) {
  const pct = discountPct(deal);
  if (pct) return `${pct}% OFF`;
  if (deal.price == null && deal.headline) return deal.headline;
  if (best) return "Best Price";
  if (deal.pointsOffer) return "Points Deal";
  return "Weekly Special";
}
function SaleRibbon() {
  return <span style={styles.ribbon} aria-hidden="true">SALE</span>;
}

// ─── Hero card: flyer treatment, bigger ──────────────────────────────────
// Vibrant color panel with SALE ribbon + savings headline, product photo
// in a white inset, white footer with store, name and dominant price.
function HeroCard({ deal, pct, saved, onSave }) {
  const color = cardColor(deal);
  return (
    <div className="pressable" style={styles.heroCard}>
      <div style={{ ...styles.flyerPanel, background: color }}>
        <SaleRibbon />
        <div style={styles.flyerHead}>Save {Math.round(pct * 100)}%</div>
        <div style={styles.flyerPhotoHero}>
          <FlippArt deal={deal} iconSize={72} />
          <button
            onClick={onSave}
            aria-label={saved ? "Saved to grocery list" : "Save to grocery list"}
            aria-pressed={!!saved}
            style={{ ...styles.saveBtn, ...(saved ? styles.saveBtnSaved : null) }}
          >
            <span key={String(!!saved)} style={styles.savePop}>
              <IconBookmark size={15} filled={!!saved} />
            </span>
          </button>
        </div>
      </div>
      <div style={{ padding: "11px 14px 14px" }}>
        <div style={styles.heroStore}>{deal.store}</div>
        <div style={styles.heroItem}>
          {deal.item}
          {deal.size && <span style={{ color: T.textMuted, fontWeight: 500 }}> · {deal.size}</span>}
        </div>
        <div style={{ marginTop: 6 }}>
          <PriceBlock deal={deal} priceSize={24} />
        </div>
        <div style={styles.heroDates}>{fmtRange(deal.validFrom, deal.validTo)}</div>
      </div>
    </div>
  );
}

// ─── Flyer grid card (v3.8) ──────────────────────────────────────────────
// Digital-flyer anatomy: white card, vibrant inset panel with a diagonal
// SALE ribbon + bold headline, product photo in a white inset, then a
// white footer — item name, dominant price, yellow % OFF chip, store/dates.
function WeeklyCard({ deal, best, expired, km, saved, onSave }) {
  const color = cardColor(deal);
  const pct = discountPct(deal);
  return (
    <div className="pressable" style={{ ...styles.fcard, ...(expired ? styles.expiredCard : null) }}>
      <div style={{ ...styles.flyerPanel, background: color }}>
        <SaleRibbon />
        <div style={styles.flyerHead}>{flyerHeadline(deal, best)}</div>
        <div style={styles.flyerPhoto}>
          <FlippArt deal={deal} iconSize={64} />
          {best && !expired && (
            <span style={styles.bestBadge}><IconTag size={11} /> Best price</span>
          )}
          {expired && <span style={styles.expiredBadge}>Expired</span>}
          <button
            onClick={onSave}
            aria-label={saved ? "Saved to grocery list" : "Save to grocery list"}
            aria-pressed={!!saved}
            style={{ ...styles.saveBtn, ...(saved ? styles.saveBtnSaved : null) }}
          >
            <span key={String(!!saved)} style={styles.savePop}>
              <IconBookmark size={15} filled={!!saved} />
            </span>
          </button>
        </div>
      </div>
      <div style={{ padding: "10px 12px 12px" }}>
        <div style={styles.fItem}>
          {deal.item}
          {deal.size && <span style={{ color: T.textMuted, fontWeight: 500 }}> · {deal.size}</span>}
        </div>
        <div style={{ marginTop: 5, display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
          <PriceBlock deal={deal} priceSize={21} />
          {pct && !expired && <span style={styles.pctChip} className="tnum">{pct}% OFF</span>}
        </div>
        <div style={styles.fDates}>
          {deal.store} · {expired ? `Expired ${fmtDay(deal.validTo)}` : fmtRange(deal.validFrom, deal.validTo)}
        </div>
        {km != null && (
          <div style={{ marginTop: 6 }}>
            <span style={styles.wkDistChip}>
              <IconPin size={11} /> <span className="tnum">{formatDistance(km)}</span>
            </span>
          </div>
        )}
        {deal.note && (
          <div style={styles.fNote}>
            <IconInfo size={12} /> <span>{deal.note}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Compact list row ────────────────────────────────────────────────────
export function WeeklyRow({ deal, best, expired, km, saved, onSave }) {
  return (
    <div className="pressable" style={{ ...styles.lrow, ...(expired ? styles.expiredCard : null) }}>
      <div style={{ width: 62, height: 62, borderRadius: 14, overflow: "hidden", flexShrink: 0, position: "relative" }}>
        <FlippArt deal={deal} iconSize={40} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={styles.lItem}>{deal.item}</span>
          {best && !expired && (
            <span style={styles.bestBadgeInline}><IconTag size={10} /> Best</span>
          )}
          {expired && <span style={styles.expiredBadgeInline}>Expired</span>}
        </div>
        <div style={styles.lMeta}>
          {deal.store}{deal.size ? ` · ${deal.size}` : ""} · {expired ? `ended ${fmtDay(deal.validTo)}` : fmtRange(deal.validFrom, deal.validTo)}
        </div>
        {deal.note && <div style={styles.lNote}>{deal.note}</div>}
        {deal.pointsOffer && deal.pointsOffer.effectivePrice != null && (
          <div className="tnum" style={styles.effLineSmall}>
            {money(deal.pointsOffer.effectivePrice)}{deal.unit === "/lb" ? "/lb" : " ea"} after points
            {deal.pointsOffer.effectiveNote ? ` · ${deal.pointsOffer.effectiveNote}` : ""}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        {deal.price != null ? (
          <div className="tnum" style={{ fontSize: 21, fontWeight: 800, color: T.text, letterSpacing: -0.5 }}>
            {money(deal.price)}
            {deal.unit && <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>{deal.unit}</span>}
          </div>
        ) : (
          <div className="tnum" style={{ fontSize: 15, fontWeight: 800, color: T.accentDark }}>{deal.headline}</div>
        )}
        {km != null && (
          <div style={{ marginTop: 5 }}>
            <span style={styles.wkDistChip}>
              <IconPin size={11} /> <span className="tnum">{formatDistance(km)}</span>
            </span>
          </div>
        )}
        <button
          onClick={onSave}
          aria-label={saved ? "Saved to grocery list" : "Save to grocery list"}
          aria-pressed={!!saved}
          style={{ ...styles.saveBtnRow, ...(saved ? styles.saveBtnSaved : null), marginTop: 8 }}
        >
          <span key={String(!!saved)} style={styles.savePop}>
            <IconBookmark size={14} filled={!!saved} />
          </span>
        </button>
      </div>
    </div>
  );
}

// ─── Collapsible section (also reused for the closing-time feed) ────────
export function CollapsedSection({ title, count, children, hint }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <button onClick={() => setOpen((o) => !o)} style={styles.collapsedBtn} aria-expanded={open}>
        <span>
          {title} <span className="tnum" style={{ opacity: 0.6, fontWeight: 600 }}>· {count}</span>
        </span>
        <span
          style={{
            display: "inline-block", transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none", color: T.textMuted,
          }}
        >
          ▾
        </span>
      </button>
      {hint && !open && <div style={styles.collapsedHint}>{hint}</div>}
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

// ─── Weekly tab ─────────────────────────────────────────────────────────
// loc: { lat, lng } | null — when granted, deals sort nearest-store-first
// and cards show distance chips (stores without a verified coordinate get
// no chip). When off, the flyer order and store chips work as before.
// store/onStoreChange are lifted to App so the Nearby map can deep-link
// ("View all deals" filters this tab to that store).
export function WeeklyTab({ loc, savedItems = [], onSaveItem = () => {}, store = "all", onStoreChange = () => {}, streak = 0 }) {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [view, setView] = useState("flyer"); // "flyer" | "list"
  const today = useMemo(startOfToday, []);
  const bestIds = useMemo(() => computeBestIds(WEEKLY_DEALS), []);
  // v3.0: the decide-for-me lure — computed top picks, unfiltered.
  const hero = useMemo(() => heroPicks(WEEKLY_DEALS, bestIds, today), [bestIds, today]);
  const q = query.trim().toLowerCase();
  const browsing = q !== "" || store !== "all" || cat !== "all";

  // Saved-state for the per-card "save to grocery list" affordance.
  // Matches the List tab's own dedupe (case-insensitive exact text).
  const savedSet = useMemo(
    () => new Set(savedItems.map((i) => (i.text || "").toLowerCase())),
    [savedItems]
  );
  const saveProps = (d) => ({
    saved: savedSet.has((d.item || "").toLowerCase()),
    onSave: () => { buzzLight(); onSaveItem(d); },
  });

  const filtered = useMemo(
    () =>
      WEEKLY_DEALS.filter(
        (d) =>
          (store === "all" || d.storeId === store) &&
          (cat === "all" || d.category === cat) &&
          (!q || `${d.item} ${d.store} ${d.note || ""} ${d.size || ""}`.toLowerCase().includes(q))
      ).map((d) => ({ d, km: storeDistanceKm(d, loc) })),
    [store, cat, q, loc]
  );
  const live = filtered.filter((a) => !isExpired(a.d, today));
  // v3.9: counts come from ALL live deals (unfiltered), so this order is
  // stable — tapping a store highlights it on the spot instead of the
  // tapped badge jumping to position #1
  const brandCounts = useMemo(() => {
    const c = {};
    WEEKLY_DEALS.forEach((d) => {
      if (isExpired(d, today)) return;
      if (d.storeId) c[d.storeId] = (c[d.storeId] || 0) + 1;
    });
    return c;
  }, [today]);
  const expired = filtered.filter((a) => isExpired(a.d, today));
  // Location on: nearest store's deals first; stores without a verified
  // coordinate sort last. Location off: keep the flyer order.
  const ordered = loc
    ? [...live].sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity))
    : live;

  return (
    <div>
      <div style={styles.tabHead}>
        <div style={styles.tabTitleRow}>
          <div style={styles.tabTitle}>What's on sale.</div>
          {streak > 1 && (
            <span style={styles.streakPill} className="tnum" title="Consecutive days checking deals">
              <IconFlame size={13} filled /> {streak}-day streak
            </span>
          )}
        </div>
        <div style={styles.tabSub}>
          {WEEKLY_SCAN_WEEK} · Flipp flyers + r/Calgary's weekly thread
          {loc && <span> · nearest first</span>}
        </div>
      </div>

      {/* v3.2: interactive bubble logo centerpiece */}
      <div style={{ display: "flex", justifyContent: "center", margin: "4px 0 0" }}>
        <BubbleLogo store={store} onStoreChange={onStoreChange} counts={brandCounts} />
      </div>

      {/* v3.0: DECIDE FOR ME — the lure. Hidden once you start filtering. */}
      {!browsing && hero.length > 0 && (
        <div style={styles.heroWrap}>
          <div style={styles.heroHead}>
            <div style={styles.heroTitle}>This week's best</div>
            <div style={styles.heroSub}>The deepest cuts — no digging required</div>
          </div>
          <div style={styles.heroRail}>
            {hero.map(({ d, pct }) => (
              <HeroCard key={d.id} deal={d} pct={pct} {...saveProps(d)} />
            ))}
          </div>
        </div>
      )}

      <div style={styles.searchWrap}>
        <span style={{ color: T.textMuted, display: "flex" }}><IconSearch size={17} /></span>
        <input
          style={styles.searchInput}
          placeholder="Search deals, stores…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search weekly deals"
        />
        {query && (
          <button onClick={() => setQuery("")} style={styles.searchClear} aria-label="Clear search">
            ×
          </button>
        )}
      </div>

      <div style={styles.toggleRow}>
        <div style={styles.segWrap} role="tablist" aria-label="View">
          <button role="tab" aria-selected={view === "flyer"} onClick={() => setView("flyer")} style={segBtn(view === "flyer")}>
            Flyer
          </button>
          <button role="tab" aria-selected={view === "list"} onClick={() => setView("list")} style={segBtn(view === "list")}>
            List
          </button>
        </div>
        <div className="tnum" style={styles.countLine}>
          {live.length} live{expired.length > 0 ? ` · ${expired.length} expired` : ""}
        </div>
      </div>

      {/* Brand rail is the store selector: tap a brand to filter, tap again to clear */}
      <BrandRail
        active={store === "all" ? null : store}
        onPick={(b) => { buzzLight(); onStoreChange(b || "all"); }}
      />
      <div style={{ ...styles.chipRow, marginTop: 8 }}>
        <Chip active={cat === "all"} onClick={() => setCat("all")} label="All" />
        {WEEKLY_CATEGORIES.map((c) => (
          <Chip key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} label={c.label} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={styles.emptyWeek}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Mascot size={84} />
          </div>
          <div style={{ fontWeight: 800, fontSize: 17, marginTop: 10, letterSpacing: -0.2 }}>
            The shelves are empty here.
          </div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 5, lineHeight: 1.5 }}>
            Nothing matches that combo — try clearing the search
            or tapping a different store.
          </div>
        </div>
      )}

      {view === "flyer" ? (
        <div style={styles.grid}>
          {ordered.map(({ d, km }) => (
            <WeeklyCard key={d.id} deal={d} best={bestIds.has(d.id)} expired={false} km={km} {...saveProps(d)} />
          ))}
        </div>
      ) : (
        <div>
          {ordered.map(({ d, km }) => (
            <WeeklyRow key={d.id} deal={d} best={bestIds.has(d.id)} expired={false} km={km} {...saveProps(d)} />
          ))}
        </div>
      )}

      <CollapsedSection
        title="Recently expired"
        count={expired.length}
        hint="Oct 8–14 · r/Calgary thread by u/PurpleGeek — still useful for price history, tap to view"
      >
        {view === "flyer" ? (
          <div style={styles.grid}>
            {expired.map(({ d, km }) => (
              <WeeklyCard key={d.id} deal={d} best={false} expired km={km} {...saveProps(d)} />
            ))}
          </div>
        ) : (
          <div>
            {expired.map(({ d, km }) => (
              <WeeklyRow key={d.id} deal={d} best={false} expired km={km} {...saveProps(d)} />
            ))}
          </div>
        )}
      </CollapsedSection>

      <div style={styles.attrib}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 6 }}>
          <IconCheck size={13} />
          <span>
            Flyer deals verified from this week's flyer pages (Flipp) · <b>{WEEKLY_SCAN_WEEK}</b>
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          Reddit finds from{" "}
          <a href="https://www.reddit.com/r/Calgary/comments/1x69nxq/calgary_grocery_deals_october_15_to_21_2026/" target="_blank" rel="noreferrer" style={styles.attribLink}>
            r/Calgary's weekly thread by u/PurpleGeek
          </a>{" "}
          (Oct 15–21) ·{" "}
          <a href="https://www.reddit.com/r/Calgary/comments/1x0gco4/calgary_grocery_deals_october_8_to_14_2026/" target="_blank" rel="noreferrer" style={styles.attribLink}>
            previous week
          </a>{" "}
          (Oct 8–14, expired)
        </div>
        <div style={{ marginTop: 8 }}>
          Coming Tue Oct 20: Save-On-Foods $1.49 Day — items TBA.
        </div>
        <div style={{ marginTop: 8 }}>
          Prices change — confirm in-store or in the flyer before you shop.
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={chipStyle(active)}>
      {label}
    </button>
  );
}

const chipStyle = (active) => ({
  padding: "8px 14px", borderRadius: 20, whiteSpace: "nowrap",
  border: `1.5px solid ${active ? T.accent : T.border}`,
  background: active ? T.accentLight : "transparent",
  color: active ? T.accentDark : T.textSub,
  fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
});

const segBtn = (active) => ({
  flex: 1, padding: "8px 0", borderRadius: 10, border: "none", cursor: "pointer",
  background: active ? "#fff" : "transparent",
  color: active ? T.accentDark : T.textSub,
  fontSize: 13, fontWeight: 700, fontFamily: "inherit",
  boxShadow: active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
});

// ─── Styles ─────────────────────────────────────────────────────────────
const styles = {
  tabHead: { marginBottom: 16 },
  tabTitleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  tabTitle: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 28, letterSpacing: -0.5 },
  tabSub: { fontSize: 12.5, color: T.textSub, marginTop: 4 },
  // v3.0: streak pill — tasteful, quiet, a little proud of you.
  streakPill: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#FFF4E0", border: "1px solid #EBD9A0", color: "#8A5A00",
    fontSize: 12, fontWeight: 800, padding: "6px 11px", borderRadius: 20,
    whiteSpace: "nowrap", flexShrink: 0,
  },
  // v3.0: bookmark celebrates the tap — springy pop, never confetti.
  savePop: {
    display: "flex",
    animation: "softPulse 0.4s cubic-bezier(0.22,1,0.36,1)",
  },
  // ── v3.0: "This week's best" hero — Uber Eats Featured energy ──
  heroWrap: { marginBottom: 22 },
  heroHead: {
    display: "flex", alignItems: "baseline", justifyContent: "space-between",
    marginBottom: 10,
  },
  heroTitle: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 24, letterSpacing: -0.5 },
  heroSub: { fontSize: 12, color: T.textSub, fontWeight: 600 },
  heroRail: {
    display: "flex", gap: 12, overflowX: "auto",
    margin: "0 -14px", padding: "2px 14px 12px",
    scrollbarWidth: "none", WebkitOverflowScrolling: "touch",
    scrollSnapType: "x proximity",
  },
  heroCard: {
    width: 252, flexShrink: 0, scrollSnapAlign: "start",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 20,
    overflow: "hidden", boxShadow: T.shadow,
  },
  heroSavePill: {
    position: "absolute", top: 10, left: 10,
    background: "#141210", color: "#fff",
    fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
    padding: "6px 11px", borderRadius: 20,
    boxShadow: "0 1px 6px rgba(0,0,0,0.25)",
  },
  heroStore: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 1.4, color: T.textSub, marginBottom: 5,
  },
  heroItem: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, letterSpacing: -0.2 },
  heroDates: { fontSize: 11, color: T.textMuted, marginTop: 7, fontWeight: 600 },
  searchWrap: {
    display: "flex", alignItems: "center", gap: 9,
    background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: 14,
    padding: "0 14px", marginBottom: 12,
  },
  searchInput: {
    flex: 1, border: "none", outline: "none", background: "transparent",
    padding: "13px 0", fontSize: 16, color: T.text, fontFamily: "inherit",
  },
  searchClear: {
    border: "none", background: T.bgCardAlt, color: T.textSub, borderRadius: 12,
    width: 26, height: 26, fontSize: 16, cursor: "pointer", lineHeight: 1,
  },
  toggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  segWrap: {
    display: "flex", background: T.bgCardAlt, border: `1px solid ${T.border}`,
    borderRadius: 13, padding: 3, width: 168,
  },
  countLine: { fontSize: 12.5, color: T.textSub, fontWeight: 600 },
  chipRow: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" },
  // v3.8: true flyer masonry — two CSS columns, cards flow down then across
  grid: { columnCount: 2, columnGap: 12, marginTop: 14 },
  fcard: {
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18,
    overflow: "hidden", boxShadow: T.shadow,
    breakInside: "avoid", marginBottom: 12,
  },
  // v3.8 flyer anatomy
  flyerPanel: {
    position: "relative", overflow: "hidden",
    margin: 6, marginBottom: 0, borderRadius: 13,
    padding: "24px 8px 8px",
  },
  ribbon: {
    position: "absolute", top: 13, left: -27, zIndex: 3,
    transform: "rotate(-38deg)",
    background: "#D92323", color: "#fff",
    fontSize: 10, fontWeight: 900, letterSpacing: 1.6,
    padding: "3px 30px",
    boxShadow: "0 2px 5px rgba(0,0,0,.25)",
    pointerEvents: "none",
  },
  flyerHead: {
    color: "#fff", fontSize: 17.5, fontWeight: 900, letterSpacing: -0.2,
    textAlign: "center", lineHeight: 1.15, minHeight: 20,
    textShadow: "0 1px 3px rgba(0,0,0,.18)",
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
  },
  flyerPhoto: {
    position: "relative", marginTop: 8, borderRadius: 10, overflow: "hidden",
    aspectRatio: "4 / 3", background: "#fff",
  },
  flyerPhotoHero: {
    position: "relative", marginTop: 8, borderRadius: 10, overflow: "hidden",
    aspectRatio: "16 / 9", background: "#fff",
  },
  pctChip: {
    background: "#FFC61A", color: "#8F1D1D",
    fontSize: 11.5, fontWeight: 900, letterSpacing: 0.3,
    padding: "4px 9px", borderRadius: 8, whiteSpace: "nowrap",
    marginTop: 3,
  },
  expiredCard: { filter: "grayscale(0.9)", opacity: 0.78 },
  storeBadge: {
    position: "absolute", top: 8, left: 8,
    background: "rgba(255,255,255,0.94)", color: T.text,
    fontSize: 10.5, fontWeight: 700, padding: "4px 9px", borderRadius: 20,
    maxWidth: "62%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  bestBadge: {
    position: "absolute", top: 8, right: 8,
    display: "inline-flex", alignItems: "center", gap: 4,
    // v3.0: delivery-app promo badge — one confident black pill.
    background: "#141210", color: "#fff",
    fontSize: 10.5, fontWeight: 700, padding: "5px 10px", borderRadius: 20,
    boxShadow: "0 1px 6px rgba(0,0,0,0.25)",
  },
  bestBadgeInline: {
    display: "inline-flex", alignItems: "center", gap: 3,
    background: T.goldBg, color: T.gold, border: "1px solid #EBD9A0",
    fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
  },
  // Quiet "save to grocery list" affordance — Luma restraint, MetaLab feel.
  saveBtn: {
    position: "absolute", bottom: 8, right: 8,
    width: 32, height: 32, borderRadius: 16,
    background: "rgba(255,255,255,0.94)", border: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: T.textSub,
    boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
  },
  saveBtnRow: {
    width: 30, height: 30, borderRadius: 15,
    background: T.bgCardAlt, border: `1px solid ${T.border}`,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: T.textSub,
  },
  saveBtnSaved: {
    color: T.accentDark,
  },
  expiredBadge: {
    position: "absolute", top: 8, right: 8,
    background: T.red, color: "#fff",
    fontSize: 10.5, fontWeight: 700, padding: "4px 9px", borderRadius: 20,
  },
  expiredBadgeInline: {
    background: T.redBg, color: T.red,
    fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
  },
  fItem: {
    // v3.0: Uber Eats dish-card anatomy — the name carries the card.
    fontSize: 15, fontWeight: 700, lineHeight: 1.3,
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
    minHeight: 39,
  },
  fDates: { fontSize: 11, color: T.textMuted, marginTop: 6, fontWeight: 600 },
  fNote: {
    display: "flex", gap: 5, alignItems: "flex-start",
    fontSize: 11.5, color: T.textSub, fontStyle: "italic", marginTop: 6, lineHeight: 1.4,
  },
  effLine: { fontSize: 13, fontWeight: 700, color: T.accent, marginTop: 2 },
  effLineSmall: { fontSize: 11.5, fontWeight: 700, color: T.accent, marginTop: 3 },
  // Mirrors the home tab's distance chip (components.js distChip).
  wkDistChip: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
    background: "#EAF5EE", color: T.accentDark, border: `1px solid ${T.accentBorder}`,
    whiteSpace: "nowrap",
  },
  offerLine: { fontSize: 12, fontWeight: 600, color: T.textSub, marginTop: 3 },
  offerNote: { fontSize: 11, color: T.textMuted, marginTop: 2, lineHeight: 1.4 },
  lrow: {
    display: "flex", gap: 12, alignItems: "center",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16,
    padding: 12, marginBottom: 10, boxShadow: T.shadow,
  },
  lItem: { fontSize: 14, fontWeight: 700, lineHeight: 1.3 },
  lMeta: { fontSize: 11.5, color: T.textSub, marginTop: 3 },
  lNote: { fontSize: 11.5, color: T.textSub, fontStyle: "italic", marginTop: 3, lineHeight: 1.4 },
  emptyWeek: {
    textAlign: "center", color: T.textSub, padding: "40px 20px",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18, marginTop: 14,
  },
  collapsedBtn: {
    width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
    background: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 14,
    padding: "13px 16px", fontSize: 15, fontWeight: 800, letterSpacing: -0.2, color: T.text,
    cursor: "pointer", fontFamily: "inherit",
  },
  collapsedHint: { fontSize: 11.5, color: T.textMuted, marginTop: 6, paddingLeft: 4 },
  attrib: {
    fontSize: 12, color: T.textMuted, textAlign: "center", lineHeight: 1.6,
    marginTop: 26, padding: "0 16px",
  },
  attribLink: { color: T.accent, fontWeight: 700, textDecoration: "none" },
};
